import React from 'react';
import { Typography, Button, Box } from '@mui/material';
import { Prompt } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { sortBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { api } from 'common/frontend/api';
import { OpeningHoursApi, ExceptionPeriod, LocationOpeningHoursApi } from 'common/types';
import { useTranslation } from 'services/localization';
import { getExceptionPeriodsBasedOnLocation } from 'common/utils/shopUtils';
import { setOnboardingActionDone } from 'services/onboarding/utils';
import { getNewExceptionPeriod, validateExceptionPeriodDates, isMainOpeningHours } from './utils';

import * as UserSelectors from 'selectors/UserSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';

import SaveButton from 'common/components/SaveButton';
import SetPageTitle from 'components/Portals/SetPageTitle';
import ContainerBox from 'components/ContainerBox';
import Banner from 'components/Banner';
import EmptyState from 'common/components/states/EmptyState';
import OpeningHours from './OpeningHours';
import ExceptionPeriods from './ExceptionPeriods';
import OpeningHoursSummary from './OpeningHoursSummary';
import { notUndefined } from 'common/utils/common';

const EMPTY_OPENING_HOURS = {
	defaultTimes: [],
	exceptionDates: [],
	openingHoursWithLocation: {},
	useDefault: false,
};

const DEFAULT_OPENING_HOURS = {
	defaultTimes: [],
	exceptionDates: [],
	openingHoursWithLocation: {},
	useDefault: true,
};

type FormValues = {
	locationOpeningHours: OpeningHoursApi | LocationOpeningHoursApi;
	locationExceptionPeriods: ExceptionPeriod[];
};

const OpeningHoursTab = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId)!;
	const mainLocationId = useSelector(ShopSelectors.activeShopMainLocationId);
	const locationsById = useSelector(ShopSelectors.allShopLocationsById);
	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo);
	const shopExceptionPeriods = useSelector(ShopSelectors.activeShopExceptionPeriods);
	const shopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);
	const shopOpeningHours = shopPublicInfo?.openingHours ?? DEFAULT_OPENING_HOURS;
	const isMainLocation = activeLocationId === mainLocationId;

	const activeLocationOpeningHours = isMainLocation
		? shopOpeningHours
		: shopOpeningHours.openingHoursWithLocation?.[activeLocationId] ?? EMPTY_OPENING_HOURS;

	const activeLocationExceptionPeriods = sortBy(
		getExceptionPeriodsBasedOnLocation(
			shopExceptionPeriods,
			activeLocationId,
			mainLocationId,
			shopOpeningHours,
		),
		'startDate',
	);

	const defaultValues: FormValues = {
		locationOpeningHours: activeLocationOpeningHours,
		locationExceptionPeriods: activeLocationExceptionPeriods,
	};
	const form = useForm<FormValues>({
		defaultValues,
	});

	const { isDirty, dirtyFields, isSubmitting } = form.formState;

	const areExceptionPeriodDatesValid = (exceptionPeriods: ExceptionPeriod[]) => {
		return exceptionPeriods.every((period, index) =>
			validateExceptionPeriodDates(period, exceptionPeriods.slice(0, index)),
		);
	};

	const handleSubmit = async (data: FormValues) => {
		if (!areExceptionPeriodDatesValid(data.locationExceptionPeriods)) {
			dispatch(
				NotificationActions.showNotification({
					message: t(
						'openingHours.overlappingPeriodsError',
						'Please make sure you have no overlapping exception periods and try again',
					),
					variant: 'error',
				}),
			);
			return;
		}

		try {
			if (dirtyFields.locationExceptionPeriods) {
				const existingExceptionPeriodIds = activeLocationExceptionPeriods.map(
					(period) => period.id,
				);
				const updatedExceptionPeriodIds = data.locationExceptionPeriods.map((period) => period.id);

				const exceptionPeriodIdsToRemove = existingExceptionPeriodIds.filter(
					(id) => !updatedExceptionPeriodIds.includes(id),
				);

				exceptionPeriodIdsToRemove.map(async (id) => {
					await api().shops.doc(shopId).exceptionPeriods.doc(id).delete();
				});

				data.locationExceptionPeriods.map(async (period) => {
					await api().shops.doc(shopId).exceptionPeriods.doc(period.id).set(period);
				});
			}

			if (dirtyFields.locationOpeningHours) {
				if (activeLocationId === mainLocationId) {
					await api().shops.doc(shopId).public.update({
						'openingHours.defaultTimes': data.locationOpeningHours.defaultTimes,
						'openingHours.exceptionDates': data.locationOpeningHours.exceptionDates,
						'openingHours.useDefault': true,
					});
				} else {
					const basePath = `openingHours.openingHoursWithLocation.${activeLocationId}` as `openingHours.openingHoursWithLocation.${123}`;
					await api()
						.shops.doc(shopId)
						.public.update({
							[`${basePath}.defaultTimes` as `${typeof basePath}.defaultTimes`]: data
								.locationOpeningHours.defaultTimes,
							[`${basePath}.exceptionDates` as `${typeof basePath}.exceptionDates`]: data
								.locationOpeningHours.exceptionDates,
							[`${basePath}.useDefault` as `${typeof basePath}.useDefault`]: data
								.locationOpeningHours.useDefault,
						});
				}
			}
			setOnboardingActionDone('OPENING_HOURS', shopAnalytics);
			form.reset(data);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	return (
		<>
			<SetPageTitle
				title={t('store.storeSettings', 'Store settings')}
				priority={2}
				rightContent={
					<SaveButton
						disabled={!isDirty}
						loading={isSubmitting}
						onClick={form.handleSubmit(handleSubmit)}
						t={t}
					/>
				}
			/>
			<Prompt
				when={isDirty}
				message={() =>
					t(
						'common:prompts.unsavedChanges',
						'You have unsaved changes - are you sure you want to leave this page?',
					)
				}
			/>
			<Controller
				control={form.control}
				name="locationOpeningHours"
				render={(renderProps) => {
					const renderBanner = () => {
						const openingHours = renderProps.field.value;
						if (isMainLocation && isMainOpeningHours(openingHours)) {
							const locationIdsUsingDefault = Object.entries(
								openingHours.openingHoursWithLocation ?? {},
							)
								.filter(([, { useDefault }]) => useDefault)
								.map(([id]) => id);

							if (locationIdsUsingDefault.length > 0) {
								return (
									<Banner
										title={t(
											'openingHours.editingDefaultStoreHours',
											'Editing default store opening hours',
										)}
										description={t('openingHours.followingStoresUsingHours', {
											stores: locationIdsUsingDefault
												.map((id) => locationsById[id]?.name)
												.filter(notUndefined)
												.join(', '),
											defaultValue:
												'The following stores are also using these opening hours: {{stores}}',
										})}
										variant="info"
									/>
								);
							}
						} else {
							const mainLocationName = locationsById[mainLocationId]?.name;
							if (openingHours.useDefault) {
								return (
									<ContainerBox>
										<EmptyState
											title={t(
												'openingHours.usingDefaultStoreHours',
												'Using default store opening hours',
											)}
											description={t('openingHours.usingDefaultStoreHoursDescription', {
												defaultLocation: mainLocationName,
												defaultValue:
													'This store is using the opening hours of your default location ({{defaultLocation}})',
											})}
											cta={
												<>
													<OpeningHoursSummary openingHours={shopOpeningHours} />
													<Box mb={2} />
													<Button
														color="primary"
														variant="outlined"
														onClick={() =>
															renderProps.field.onChange({
																// Always reset to default opening hours, as a basis for customisation
																useDefault: false,
																defaultTimes: shopOpeningHours.defaultTimes,
																exceptionDates: shopOpeningHours.exceptionDates,
																openingHoursWithLocation: {},
															})
														}
													>
														{t('common:actions.customize', 'Customize')}
													</Button>
												</>
											}
										/>
									</ContainerBox>
								);
							} else {
								return (
									<Banner
										title={t('openingHours.usingCustomHours', 'Using custom opening hours')}
										description={t('openingHours.usingCustomHoursDescription', {
											defaultLocation: mainLocationName,
											defaultValue:
												'You can also set this store to use the opening hours of your default store ({{defaultLocation}})',
										})}
										variant="info"
										button={{
											text: t('openingHours.useDefaultHours', 'Use default hours'),
											onClick: () =>
												renderProps.field.onChange({
													// No need to keep the unused defaultTimes and exceptionDates here
													useDefault: true,
													defaultTimes: [],
													exceptionDates: [],
													openingHoursWithLocation: {},
												}),
										}}
									/>
								);
							}
						}
						return null;
					};

					const usesDefaultHours = !isMainLocation && renderProps.field.value.useDefault;

					const renderDefaultHours = () => {
						if (usesDefaultHours) return <></>;
						return (
							<ContainerBox>
								<Typography variant="h6">
									{t('openingHours.defaultHours', 'Default hours')}
								</Typography>
								<OpeningHours
									value={renderProps.field.value}
									onChange={renderProps.field.onChange}
								/>
							</ContainerBox>
						);
					};

					const renderExceptionPeriods = () => {
						return (
							<Controller
								control={form.control}
								name="locationExceptionPeriods"
								render={(renderProps) => {
									if (usesDefaultHours) return <></>;
									const addExceptionPeriod = () => {
										const newExceptionPeriod = getNewExceptionPeriod(
											renderProps.field.value,
											activeLocationId,
										);
										renderProps.field.onChange([...renderProps.field.value, newExceptionPeriod]);
										setTimeout(() => {
											window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
										}, 0);
									};

									return (
										<>
											<Box p={2}>
												<Typography variant="h6">
													{t('openingHours.exceptionPeriods', 'Exception periods')}
												</Typography>
												<Typography variant="body2">
													{t(
														'openingHours.exceptionPeriodsDescription',
														'Time periods with different opening hours (e.g. summer holiday). These will override the default hours for the given time periods.',
													)}
												</Typography>
												<Box mt={3} />
												<Button variant="outlined" color="primary" onClick={addExceptionPeriod}>
													{t('openingHours.addExceptionPeriod', 'Add exception period')}
												</Button>
											</Box>
											<ExceptionPeriods
												value={renderProps.field.value}
												onChange={renderProps.field.onChange}
												locationId={activeLocationId}
											/>
										</>
									);
								}}
							/>
						);
					};

					return (
						<>
							{renderBanner()}
							{renderDefaultHours()}
							{renderExceptionPeriods()}
						</>
					);
				}}
			/>
		</>
	);
};

export default OpeningHoursTab;
